import React, { useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { mergeTimeSeries, toMWh } from "./utils.js";

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  Filler,
);

function getGraphColors() {
  const root = getComputedStyle(document.documentElement);
  return {
    measured: root.getPropertyValue("--brand-yellow").trim(),
    forecasted: root.getPropertyValue("--brand-navy").trim(),
    price: root.getPropertyValue("--brand-green").trim(),
    co2: root.getPropertyValue("--brand-black").trim(),
  };
}

function withAlpha(cssColor, alpha = 0.5) {
  const c = cssColor.trim();

  if (c.startsWith("rgba(") || c.startsWith("hsla(")) return c;

  if (c.startsWith("#")) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split("").map((ch) => ch + ch).join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  if (c.startsWith("rgb(")) return c.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);

  return c;
}


const dsBar = ({ label, data, color, yAxisID = "y", stack, unit, order }) => ({
  type: "bar",
  label,
  data,
  backgroundColor: color,
  borderRadius: 2,
  maxBarThickness: 12,
  yAxisID,
  stack,
  _unit: unit,
  order,
});

const dsLine = ({
  label,
  data,
  color,
  yAxisID = "y",
  unit,
  order,
  pointRadius,
  fill = false,
}) => ({
  type: "line",
  label,
  data,
  borderColor: color,
  backgroundColor: fill ? withAlpha(color, 0.5) : color,
  tension: 0.3,
  pointRadius,
  borderWidth: fill ? 0 : 2,
  fill: fill ? "origin" : false,
  yAxisID,
  _unit: unit,
  order,
});

function sortByTimeUTC(arr) {
  return [...arr].sort(
    (a, b) =>
      DateTime.fromISO(a.time, { zone: "utc" }).toMillis() -
      DateTime.fromISO(b.time, { zone: "utc" }).toMillis()
  );
}

// intervalMinutes: 60 (hourly, default) or 15 (15-min), etc.
function generateFullDayLabels(userTimezone = "UTC", referenceDate, intervalMinutes = 60) {
  let dayStart;

  if (referenceDate) {
    dayStart = DateTime
      .fromISO(referenceDate, { zone: "utc" })
      .setZone(userTimezone)
      .startOf("day");
  } else {
    dayStart = DateTime.now().setZone(userTimezone).startOf("day");
  }

  const pointsPerDay = Math.floor(24 * 60 / intervalMinutes);
  const fmt = intervalMinutes % 60 === 0 ? "HH:00" : "HH:mm";

  return Array.from({ length: pointsPerDay }, (_, i) =>
    dayStart.plus({ minutes: i * intervalMinutes }).toFormat(fmt)
  );
}

function alignDataToFullDay(rows, key, mapFn, userTimezone = "UTC", intervalMinutes = 60) {
  const pointsPerDay = Math.floor(24 * 60 / intervalMinutes);
  if (!rows?.length) return Array(pointsPerDay).fill(null);

  const fmt = intervalMinutes % 60 === 0 ? "HH:00" : "HH:mm";

  // Floor a DateTime to the interval bucket (e.g. 60m, 15m)
  const floorToInterval = (dt) => {
    const flooredMinute = Math.floor(dt.minute / intervalMinutes) * intervalMinutes;
    return dt.set({ minute: flooredMinute, second: 0, millisecond: 0 });
  };

  // Map row values into time-of-day buckets in the user timezone
  // (if multiple rows fall into same bucket, last one wins)
  const rowMap = new Map();
  for (const r of rows) {
    const dtLocal = DateTime.fromISO(r.time, { zone: "utc" }).setZone(userTimezone);
    const bucket = floorToInterval(dtLocal).toFormat(fmt);
    const value = mapFn ? mapFn(r) : (r[key] ?? null);
    rowMap.set(bucket, value);
  }

  // Build aligned array for the day containing rows[0]
  const dayStart = DateTime.fromISO(rows[0].time, { zone: "utc" })
    .setZone(userTimezone)
    .startOf("day");

  return Array.from({ length: pointsPerDay }, (_, i) => {
    const label = dayStart.plus({ minutes: i * intervalMinutes }).toFormat(fmt);
    return rowMap.get(label) ?? null;
  });
}

export function SeriesChart({
  rows,
  series,
  chartKind = "bar",
  chartTitle,
  yAxisName,
  yAxisRightName,
  unit,
  userTimezone = "UTC",
  graphHeight,
  intervalMinutes = 60
}) {
  const { t } = useTranslation();
  const colors = getGraphColors();
  const sorted = sortByTimeUTC(rows);
  const labels = generateFullDayLabels(userTimezone, sorted[0]?.time, intervalMinutes);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const hasRightAxis = series.some((s) => s.axis === "y1");
  const leftSeries = series.filter(s => (s.axis || "y") !== "y1");
  const rightSeries = series.filter(s => s.axis === "y1");

  const extractVals = (serArray) =>
    serArray
      .flatMap(s => alignDataToFullDay(sorted, s.key, s.mapFn, userTimezone, intervalMinutes))
      .map(Number)
      .filter(Number.isFinite);

  let leftMin, leftMax, rightMin, rightMax;
  const leftVals = extractVals(leftSeries);
  leftMin = leftVals.length ? Math.min(...leftVals) : 0;
  leftMax = leftVals.length ? Math.max(...leftVals) : 1;
  if (leftMin === leftMax) { leftMin -= 1; leftMax += 1; }

  if (rightSeries.length) {
    const rightVals = extractVals(rightSeries);
    rightMin = rightVals.length ? Math.min(...rightVals) : 0;
    rightMax = rightVals.length ? Math.max(...rightVals) : 1;
    if (rightMin === rightMax) { rightMin -= 1; rightMax += 1; }
  }

  const nice = (x, step = 10) => Math.sign(x) * Math.ceil(Math.abs(x) / step) * step;

  let finalLeftMin = nice(leftMin);
  let finalLeftMax = nice(leftMax);
  if (finalLeftMin > 0) finalLeftMin = 0;

  let finalRightMin = undefined;
  let finalRightMax = undefined;

  if (rightSeries.length) {
    let rawRightMin = nice(rightMin);
    let rawRightMax = nice(rightMax);
    if (rawRightMin > 0) rawRightMin = 0;

    // compute zero fraction on left axis (fraction of range above zero)
    const leftSpan = finalLeftMax - finalLeftMin || 1;
    const leftAbove = Math.max(0, finalLeftMax - 0);
    const leftZeroFrac = leftAbove / leftSpan; // value in [0,1]; position of zero measured from bottom

    // compute minimal right span (must contain actual data)
    const rightDataSpan = rawRightMax - rawRightMin || 1;

    // choose right span initially as the minimal data span
    let chosenRightSpan = rightDataSpan;

    // iteratively ensure coverage (usually one pass is enough)
    let iter = 0;
    while (iter < 4) {
      const candMin = -chosenRightSpan * (1 - leftZeroFrac);
      const candMax = chosenRightSpan * leftZeroFrac;
      if (candMin <= rightMin && candMax >= rightMax) {
        finalRightMin = candMin;
        finalRightMax = candMax;
        break;
      }
      const shortfallBelow = Math.max(0, rightMin - candMin);
      const shortfallAbove = Math.max(0, rightMax - candMax);
      const needed = Math.max(shortfallBelow, shortfallAbove);
      chosenRightSpan += needed;
      iter += 1;
    }

    if (finalRightMin === undefined || finalRightMax === undefined) {
      finalRightMin = rawRightMin;
      finalRightMax = rawRightMax;
    }

    finalRightMin = nice(finalRightMin);
    finalRightMax = nice(finalRightMax);

    // keep 0 visible on right if entire data positive but maintain zero alignment
    if (finalRightMin > 0) finalRightMin = 0;
  }

  const datasets = series.map((s) => {
    const color = colors[s.colorKey] || colors.measured;
    const data = alignDataToFullDay(sorted, s.key, s.mapFn, userTimezone, intervalMinutes);
    const yAxisID = s.axis === "y1" ? "y1" : "y";
    return (s.type || chartKind) === "line"
      ? dsLine({
        label: t(s.label),
        data,
        color,
        yAxisID,
        unit: s.unit,
        order: s.order,
        pointRadius: s.pointRadius ?? 2,
        fill: !!s.fill,
      })
      : dsBar({
        label: t(s.label),
        data,
        color,
        yAxisID,
        stack: s.stack,
        unit: s.unit,
        order: s.order,
      });
  });

  const axisUnitRight = series.find(s => s.axis === "y1")?.unit;
  const anyStack = series.some((s) => (s.type || chartKind) !== "line" && s.stack);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    ...(isMobile ? { interaction: { enabled: false } } : {}),
    plugins: {
      legend: { display: true, position: "bottom", labels: { usePointStyle: true, pointStyle: "rect" } },
      title: { display: true, text: t(chartTitle), font: { size: 18 }, padding: { top: 10, bottom: 20 } },
      tooltip: {
        enabled: !isMobile,
        callbacks: {
          label: (ctx) => {
            const unitKey = ctx.dataset._unit ?? unit;
            const u = unitKey ? t(unitKey) : "";
            const y = ctx.parsed?.y;
            const v = typeof y === "number" ? y.toFixed(2) : y ?? "—";
            return `${ctx.dataset.label}: ${v} ${u}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t("Graphs-Hour of Day") },
        ...(anyStack ? { stacked: true } : {}),
        grid: {
          drawOnChartArea: true,
          lineWidth: (ctx) => {
            const index = ctx.index;
            const step = intervalMinutes === 15 ? 4 : 1;
            return index % step === 0 ? 1 : 0;
          },
        },
        ticks: {
          autoSkip: false,
          callback: function (value) {
            const i = Number(value);
            const step = intervalMinutes === 15 ? 4 : 1;

            if (i % step !== 0) return "";

            const hour = Math.floor(i / step);
            return `${String(hour).padStart(2, "0")}:00`;
          },
        },

      },
      y: {
        ...(finalLeftMin !== undefined && finalLeftMax !== undefined ? { min: finalLeftMin, max: finalLeftMax } : {}),
        title: { display: true, text: `${t(yAxisName)} (${unit})` },
        ticks: { precision: 6 },
        ...(anyStack ? { stacked: true } : {}),
      },
      ...(hasRightAxis
        ? {
          y1: {
            ...(finalRightMin !== undefined && finalRightMax !== undefined ? { min: finalRightMin, max: finalRightMax } : {}),
            position: "right",
            grid: { drawOnChartArea: false },
            title: { display: true, text: `${t(yAxisRightName)} (${axisUnitRight})` },
          },
        }
        : {}),
    },
  };

  return (
    <div style={{ width: "100%", height: graphHeight ? `${graphHeight}vh` : "50vh" }}>
      {chartKind === "line" ? (
        <Line data={{ labels, datasets }} options={options} />
      ) : (
        <Bar data={{ labels, datasets }} options={options} />
      )}
    </div>
  );
}

export const LoadChart = ({ load, chartType = "bar", userTimezone = "UTC" }) => (
  <SeriesChart
    rows={load}
    chartKind={chartType}
    chartTitle="Graphs-Load"
    yAxisName="Graphs-Power"
    unit="W"
    userTimezone={userTimezone}
    intervalMinutes={15}
    series={[
      { label: "Graphs-Load", key: "average_power", colorKey: "measured" },
      { label: "Graphs-Load forecast", key: "forecasted_power", colorKey: "forecasted" },
    ]}
  />
);

export const BatteryChart = ({ batteryPower, chartType = "bar", userTimezone = "UTC" }) => (
  <SeriesChart
    rows={batteryPower}
    chartKind={chartType}
    chartTitle="Graphs-Battery"
    yAxisName="Graphs-Power"
    unit="W"
    userTimezone={userTimezone}
    intervalMinutes={15}
    series={[
      { label: "Graphs-Battery Schedule Primary", key: "forecasted_power", colorKey: "forecasted", type: "line", pointRadius: 1 },
      { label: "Graphs-Battery Schedule Green", key: "secondary_forecasted_power", colorKey: "price", type: "line", pointRadius: 1 },
      { label: "Graphs-Battery", key: "average_power", colorKey: "measured", type: "line", fill: true, pointRadius: 0 },
    ]}
  />
);

export const PricesChart = ({ prices, carbon_intensity, chartType="line", userTimezone="UTC" }) => {
  const rows = useMemo(() => mergeTimeSeries({
    userTimezone,
    intervalMinutes: 15,
    sources: [
      { rows: prices, apply: (p, out) => { out.price = toMWh(p.price); } },
      { rows: carbon_intensity, apply: (c, out) => { out.co2_intensity_g_per_kwh = Number((c.carbon_intensity_value * 1000).toFixed(2)); } },
    ],
  }), [prices, carbon_intensity, userTimezone]);
  return (
    <SeriesChart
      rows={rows}
      chartKind={chartType}
      chartTitle="Graphs-DA Price & Carbon"
      yAxisName="Graphs-Price"
      yAxisRightName="Graphs-CO2"
      unit="€/MWh"
      userTimezone={userTimezone}
      intervalMinutes={15}
      series={[
        { label: "Graphs-DA Prices", key: "price", type: "line", colorKey: "price", axis: "y", unit: "€/MWh", pointRadius: 1 },
        { label: "performance.overview.series.co2.label", key: "co2_intensity_g_per_kwh", type: "line", colorKey: "co2", axis: "y1", unit: "gCO₂eq/kWh", pointRadius: 1 },
      ]}
    />
  );
};

export const GridChart = ({ grid, chartType = "bar", userTimezone = "UTC" }) => (
  <SeriesChart
    rows={grid}
    chartKind={chartType}
    chartTitle="Graphs-Grid"
    yAxisName="Graphs-Power"
    unit="W"
    userTimezone={userTimezone}
    intervalMinutes={15}
    series={[{ label: "Graphs-Grid", key: "average_value", colorKey: "measured" }]}
  />
);

export const SolarChart = ({ solarValue, chartType = "bar", userTimezone = "UTC" }) => (
  <SeriesChart
    rows={solarValue}
    chartKind={chartType}
    chartTitle="Graphs-Solar"
    yAxisName="Graphs-Power"
    unit="Wh"
    userTimezone={userTimezone}
    intervalMinutes={15}
    series={[
      { label: "Graphs-Solar", key: "average_power", colorKey: "measured" },
      { label: "Graphs-Solar Forecast", key: "forecasted_power", colorKey: "forecasted" },
    ]}
  />
);
